// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useMemo, useState } from "react"
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  type ColumnDef,
  type OnChangeFn,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table"
import { cn } from "@sglara/cn"
import { readMeta, sortWithNullsLast, type SortKey } from "@/lib/column-meta"
import { normalizeWidths } from "@/lib/column-widths"
import { clampPage } from "@/lib/table-url-state"

export interface SmartTableProps<TData> {
  data: TData[]
  columns: ColumnDef<TData>[]
  pageSize?: number
  // Sorting: controlled when `sorting`/`onSortingChange` are supplied, else
  // internal, seeded once by `initialSorting`.
  sorting?: SortingState
  onSortingChange?: (next: SortingState) => void
  initialSorting?: SortingState
  // Pagination (0-based pageIndex): controlled when `pageIndex` is supplied.
  pageIndex?: number
  onPageIndexChange?: (next: number) => void
  // Column visibility: controlled when `columnVisibility` is supplied, else
  // internal, seeded once by `initialColumnVisibility`.
  columnVisibility?: VisibilityState
  onColumnVisibilityChange?: (next: VisibilityState) => void
  initialColumnVisibility?: VisibilityState
  onRowClick?: (row: TData) => void
  rowClassName?: (row: TData) => string | undefined
}

const columnId = <TData,>(column: ColumnDef<TData>): string =>
  (column.id ?? (column as { accessorKey?: string }).accessorKey ?? "") as string

const headClass =
  "h-auto select-none px-1.5 py-[11px] font-mono text-[10.5px] font-normal uppercase tracking-[0.05em] text-text-tertiary transition-colors first:pl-[18px] last:pr-[18px]"

const cellBaseClass =
  "overflow-hidden px-1.5 py-[13px] text-[13px] text-ellipsis text-text-secondary first:pl-[18px] last:pr-[18px]"

const pageButtonClass =
  "inline-flex h-8 min-w-8 items-center justify-center rounded-[9px] border border-border-strong bg-table-pager px-2.5 font-mono text-xs text-text-secondary transition-colors hover:border-border-active hover:text-text-primary disabled:pointer-events-none disabled:opacity-35"

export default function SmartTable<TData>({
  data,
  columns,
  pageSize = 10,
  sorting: controlledSorting,
  onSortingChange,
  initialSorting = [],
  pageIndex: controlledPageIndex,
  onPageIndexChange,
  columnVisibility: controlledVisibility,
  onColumnVisibilityChange,
  initialColumnVisibility = {},
  onRowClick,
  rowClassName,
}: SmartTableProps<TData>) {
  const [internalSorting, setInternalSorting] =
    useState<SortingState>(initialSorting)
  const [internalPageIndex, setInternalPageIndex] = useState(0)
  const [internalVisibility, setInternalVisibility] = useState<VisibilityState>(
    initialColumnVisibility,
  )

  const sorting = controlledSorting ?? internalSorting
  const columnVisibility = controlledVisibility ?? internalVisibility

  const setSorting = (next: SortingState) =>
    onSortingChange ? onSortingChange(next) : setInternalSorting(next)
  const setPageIndex = (next: number) =>
    onPageIndexChange ? onPageIndexChange(next) : setInternalPageIndex(next)
  const setVisibility = (next: VisibilityState) =>
    onColumnVisibilityChange
      ? onColumnVisibilityChange(next)
      : setInternalVisibility(next)

  // Sorting is TanStack-tracked but resolved here: the nullsLast comparator
  // (a missing value sinks in both directions) is not expressible through a
  // pairwise sortingFn TanStack inverts for desc, so the row order is derived
  // with `sortWithNullsLast` and fed in under `manualSorting`.
  const sortedData = useMemo(() => {
    const active = sorting[0]
    if (!active) return data
    const column = columns.find((candidate) => columnId(candidate) === active.id)
    if (!column) return data
    const meta = readMeta<TData>(column.meta)
    const accessorFn = (column as { accessorFn?: (row: TData) => unknown })
      .accessorFn
    const getKey: (row: TData) => SortKey =
      meta.sortAccessor ??
      ((row) => accessorFn?.(row) as SortKey)
    return sortWithNullsLast(data, getKey, active.desc ? "desc" : "asc")
  }, [data, sorting, columns])

  const pageCount = Math.max(1, Math.ceil(sortedData.length / pageSize))
  const requestedPageIndex = controlledPageIndex ?? internalPageIndex
  const pageIndex = clampPage(requestedPageIndex, pageCount)

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next = typeof updater === "function" ? updater(sorting) : updater
    setSorting(next)
  }
  const handleVisibilityChange: OnChangeFn<VisibilityState> = (updater) => {
    const next =
      typeof updater === "function" ? updater(columnVisibility) : updater
    setVisibility(next)
  }

  const table = useReactTable({
    data: sortedData,
    columns,
    state: {
      sorting,
      columnVisibility,
      pagination: { pageIndex, pageSize },
    },
    manualSorting: true,
    // The manual comparator below applies one key; multi-sort stays off so a
    // shift-click never marks a second column the row order ignores.
    enableMultiSort: false,
    sortDescFirst: false,
    autoResetPageIndex: false,
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: handleVisibilityChange,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  const visibleColumns = table.getVisibleLeafColumns()
  const widths = normalizeWidths(
    visibleColumns.map((column) => ({
      id: column.id,
      width: readMeta<TData>(column.columnDef.meta).width,
    })),
    visibleColumns.map((column) => column.id),
  )

  const rows = table.getRowModel().rows
  const showPager = sortedData.length > 0
  const rangeStart = pageIndex * pageSize + 1
  const rangeEnd = Math.min((pageIndex + 1) * pageSize, sortedData.length)

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="w-full overflow-hidden rounded-xl border border-border bg-table-surface">
        <Table className="table-fixed">
          <TableHeader className="bg-table-header [&_tr]:border-table-header-line">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => {
                  const meta = readMeta<TData>(header.column.columnDef.meta)
                  const canSort = header.column.getCanSort()
                  const sortDir = header.column.getIsSorted()
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        headClass,
                        canSort && "cursor-pointer hover:text-text-secondary",
                        sortDir && "text-primary hover:text-primary",
                        meta.align === "right" && "text-right",
                        meta.headClass,
                      )}
                      style={{ width: `${widths[header.column.id]}%` }}
                      onClick={
                        canSort
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                    >
                      <div
                        className={cn(
                          "flex items-center gap-[5px]",
                          meta.align === "right" && "justify-end",
                        )}
                      >
                        <span className="truncate">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </span>
                        {canSort && sortDir && (
                          <span
                            aria-hidden="true"
                            className="text-[9px] text-primary"
                          >
                            {sortDir === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </div>
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className="[&_tr]:border-table-row-line">
            {rows.map((row) => (
              <TableRow
                key={row.id}
                className={cn(
                  "group transition-colors hover:bg-table-row-hover",
                  onRowClick && "cursor-pointer",
                  rowClassName?.(row.original),
                )}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
              >
                {row.getVisibleCells().map((cell) => {
                  const meta = readMeta<TData>(cell.column.columnDef.meta)
                  return (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        cellBaseClass,
                        meta.align === "right" && "text-right",
                        meta.cellClass,
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {showPager && (
        <div className="flex items-center justify-between gap-3 pt-0.5">
          <span className="font-mono text-[11.5px] text-text-tertiary">
            Showing {rangeStart}–{rangeEnd} of {sortedData.length}
          </span>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              aria-label="Previous page"
              className={pageButtonClass}
              disabled={pageIndex === 0}
              onClick={() => setPageIndex(Math.max(pageIndex - 1, 0))}
            >
              Prev
            </button>
            {Array.from({ length: pageCount }, (_, i) => i).map((index) => (
              <button
                key={index}
                type="button"
                aria-label={`Page ${index + 1}`}
                aria-current={index === pageIndex ? "page" : undefined}
                className={cn(
                  pageButtonClass,
                  index === pageIndex &&
                    "border-table-pager-active-line bg-table-pager-active text-foreground",
                )}
                onClick={() => setPageIndex(index)}
              >
                {index + 1}
              </button>
            ))}
            <button
              type="button"
              aria-label="Next page"
              className={pageButtonClass}
              disabled={pageIndex === pageCount - 1}
              onClick={() => setPageIndex(Math.min(pageIndex + 1, pageCount - 1))}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
